import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import { capitalise } from 'lib/string/stringUtils'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { accommodationIndividualRoomTileGridChildEnforcer } from './AccommodationIndividualRoomRoomTileGridBase'
import useSaleUnitFormatting from 'hooks/useSaleUnitFormatting'

export const AccommodationIndividualRoomTileHeaderContainer = styled(VerticalSpacer)`
  grid-area: header;
  ${accommodationIndividualRoomTileGridChildEnforcer}
`

type Props = {
  offer: App.HotelOffer | App.BedbankOffer
  heading: string
  packageNumber?: number
  room: OfferPageRoom
  onTitleClick?: () => void
}

function AccommodationIndividualRoomTileHeader(props: Props) {
  const {
    offer,
    packageNumber,
    heading,
    room,
    onTitleClick,
  } = props

  const intl = useIntl()
  const { formatSaleUnitLabel } = useSaleUnitFormatting()
  const capitalizedSaleUnit = formatSaleUnitLabel(capitalise(offer.saleUnit))

  const roomDetailsAriaLabel = intl.formatMessage({
    id: 'J3Sx6p8IY1mkhTCChlK1',
    defaultMessage: 'Room details',
    description: 'Aria label for the room details button/link in the multi-room selection tile header.',
  })

  const saleUnitLabel = packageNumber
    ? intl.formatMessage(
        {
          id: 'poZ0kR6DH6gTT0g1KeBC',
          defaultMessage: '{saleUnit} {number}',
          description: 'Label combining the sale unit type (e.g. "Room", "Package") with its number.',
        },
        { saleUnit: capitalizedSaleUnit, number: packageNumber },
      )
    : undefined

  return <AccommodationIndividualRoomTileHeaderContainer gap={12}>
    {!!saleUnitLabel && <div>
      <StructureLabel variant="dark">
        {saleUnitLabel}
      </StructureLabel>
    </div>}
    <div>
      <Clickable onClick={onTitleClick} aria-label={roomDetailsAriaLabel}>
        <Heading variant="heading4">
          {heading} <LineInfoCircleIcon size="S" />
        </Heading>
      </Clickable>
      <BodyText variant="medium" colour="light-foreground-alt">
        {formatOccupantsShort(room)}
      </BodyText>
    </div>
  </AccommodationIndividualRoomTileHeaderContainer>
}

export default AccommodationIndividualRoomTileHeader
